import { GeoPoint, Timestamp } from 'firebase/firestore';
/*
 * Company/store backend router.
 *
 * Modes:
 *  - online_only: Firebase/online backend only.
 *  - offline_server_only: local store gateway is authoritative for the UI.
 *  - hybrid: local-first, with explicit cloud fallback when the gateway is down.
 *
 * The configuration is persisted on the tenant under operationalSettings.offline
 * and cached in localStorage so the router is available before Settings is opened.
 */

export const SERVER_MODES = Object.freeze({
  ONLINE_ONLY: 'online_only',
  OFFLINE_SERVER_ONLY: 'offline_server_only',
  HYBRID: 'hybrid',
});

export const DEFAULT_SERVER_CONFIG = Object.freeze({
  serverMode: SERVER_MODES.ONLINE_ONLY,
  localServer: {
    enabled: false,
    url: '',
    storeId: '',
    serverId: '',
    token: '',
    timeoutMs: 3000,
    autoDiscovery: true,
    serverName: '',
    lanHostname: 'frostflow.local',
    preferredInterface: '',
    allowLanClients: true,
    requireDeviceApproval: true,
  },
  cloud: {
    enabled: true,
    allowFallback: true,
  },
  hybrid: {
    preferredSource: 'local',
    switchBackAutomatically: true,
  },
  sync: {
    enabled: true,
    automatic: true,
    intervalSeconds: 30,
    pullIntervalSeconds: 300,
    syncFiles: true,
    backgroundSync: true,
    uploadLocal: true,
    downloadCloud: true,
  },
  backup: {
    enabled: true,
    hour: 23,
    keepDaily: 14,
    keepWeekly: 8,
    keepMonthly: 12,
    includeFiles: false,
    mirrorPath: '',
  },
  security: {
    requireDeviceApproval: true,
    offlineLoginValidityDays: 30,
  },
  files: {
    storeLocallyFirst: true,
    keepLocalAfterSync: true,
    retentionDays: 0,
  },
});

const CONFIG_KEY = 'coldstorage.infrastructure.v1';
const STATUS_EVENT = 'coldstorage:infrastructure-status';
const ADMIN_TOKEN_KEY = 'coldstorage.localServerAdminToken.v1';
const LOCAL_AUTH_SESSION_KEY = 'coldstorage.localAuthSession.v1';
const nowIso = () => new Date().toISOString();
const hasWindow = () => typeof window !== 'undefined';

const safeJson = (value) => {
  if (value == null) return value;
  if (Array.isArray(value)) return value.map(safeJson);
  if (value instanceof Date) return { __firestoreTimestamp: value.toISOString() };
  if (typeof value?.toDate === 'function') {
    try { return { __firestoreTimestamp: value.toDate().toISOString() }; } catch (_) {}
  }
  if (typeof value === 'object') {
    // Preserve Firebase field transforms so the LAN database applies the same
    // intent instead of serializing private SDK objects as empty maps.
    const method = String(value?._methodName || value?.methodName || '');
    if (method === 'serverTimestamp') return { __firestoreTransform: 'serverTimestamp' };
    if (method === 'deleteField') return { __firestoreTransform: 'deleteField' };
    if (method === 'increment') return { __firestoreTransform: 'increment', operand: safeJson(value?._operand ?? value?.operand ?? 0) };
    if (method === 'arrayUnion') return { __firestoreTransform: 'arrayUnion', elements: safeJson(value?._elements || value?.elements || []) };
    if (method === 'arrayRemove') return { __firestoreTransform: 'arrayRemove', elements: safeJson(value?._elements || value?.elements || []) };
    if (typeof value.path === 'string' && value.path) return { __firestoreRef: value.path };
    if (typeof value.latitude === 'number' && typeof value.longitude === 'number') {
      return { __firestoreGeoPoint: { latitude: value.latitude, longitude: value.longitude } };
    }
    const result = {};
    Object.entries(value).forEach(([key, current]) => {
      if (typeof current !== 'function' && current !== undefined) result[key] = safeJson(current);
    });
    return result;
  }
  return value;
};

const reviveJson = (value) => {
  if (value == null) return value;
  if (Array.isArray(value)) return value.map(reviveJson);
  if (typeof value !== 'object') return value;
  if (value.__firestoreTimestamp) {
    try { return Timestamp.fromDate(new Date(value.__firestoreTimestamp)); } catch (_) { return value.__firestoreTimestamp; }
  }
  if (value.__firestoreGeoPoint) {
    try { return new GeoPoint(Number(value.__firestoreGeoPoint.latitude || 0), Number(value.__firestoreGeoPoint.longitude || 0)); } catch (_) { return value.__firestoreGeoPoint; }
  }
  if (value.__firestoreRef) return { path: value.__firestoreRef, id: String(value.__firestoreRef).split('/').filter(Boolean).pop() || '' };
  return Object.fromEntries(Object.entries(value).map(([key, current]) => [key, reviveJson(current)]));
};

const mergeConfig = (value = {}) => ({
  ...DEFAULT_SERVER_CONFIG,
  ...(value || {}),
  localServer: { ...DEFAULT_SERVER_CONFIG.localServer, ...(value?.localServer || {}) },
  cloud: { ...DEFAULT_SERVER_CONFIG.cloud, ...(value?.cloud || {}) },
  hybrid: { ...DEFAULT_SERVER_CONFIG.hybrid, ...(value?.hybrid || {}) },
  sync: { ...DEFAULT_SERVER_CONFIG.sync, ...(value?.sync || {}) },
  backup: { ...DEFAULT_SERVER_CONFIG.backup, ...(value?.backup || {}) },
  security: { ...DEFAULT_SERVER_CONFIG.security, ...(value?.security || {}) },
  files: { ...DEFAULT_SERVER_CONFIG.files, ...(value?.files || {}) },
});

const normalizeBaseUrl = (value = '') => String(value || '').trim().replace(/\/+$/, '');

const PLACEHOLDER_LOCAL_URL = 'http://192.168.1.10:8080';
const LOCAL_FAILURE_BASE_COOLDOWN_MS = 15000;
const LOCAL_FAILURE_MAX_COOLDOWN_MS = 60000;

const isLocalConfiguredValue = (config = {}) => {
  const local = config.localServer || {};
  const base = normalizeBaseUrl(local.url);
  if (local.enabled === false || !base) return false;
  const hasIdentity = Boolean(
    String(local.storeId || '').trim() ||
    String(local.serverId || '').trim() ||
    String(local.token || '').trim()
  );
  // Old builds populated the LAN placeholder for every tenant. Treat that
  // untouched placeholder as "not configured" so existing online tenants do
  // not hammer 192.168.1.10 and fail every Firestore read.
  if (base === PLACEHOLDER_LOCAL_URL && !hasIdentity) return false;
  return true;
};

let lastLocalTestAt = 0;
let localTestInFlight = null;
let localUnavailableUntil = 0;
let consecutiveLocalFailures = 0;

let state = {
  config: mergeConfig(),
  tenantId: '',
  local: { online: false, checkedAt: '', latencyMs: null, error: '' },
  cloud: { online: typeof navigator === 'undefined' ? true : navigator.onLine !== false },
  activeSource: 'cloud',
  lastError: '',
};

const loadCached = () => {
  if (!hasWindow()) return;
  try {
    const parsed = JSON.parse(window.localStorage.getItem(CONFIG_KEY) || '{}');
    state = {
      ...state,
      tenantId: String(parsed.tenantId || state.tenantId || ''),
      config: mergeConfig(parsed.config || parsed || {}),
    };
  } catch (_) {}
};
loadCached();

const configuredMode = () => state.config.serverMode || SERVER_MODES.ONLINE_ONLY;
const isLocalConfigured = () => isLocalConfiguredValue(state.config);
const circuitOpen = () =>
  configuredMode() === SERVER_MODES.HYBRID &&
  localUnavailableUntil > Date.now();

const effectiveMode = () => {
  const current = configuredMode();
  if (current === SERVER_MODES.HYBRID && !isLocalConfigured()) {
    return SERVER_MODES.ONLINE_ONLY;
  }
  return current;
};

const snapshot = () => ({
  ...state,
  config: mergeConfig(state.config),
  configuredMode: configuredMode(),
  effectiveMode: effectiveMode(),
  localConfigured: isLocalConfigured(),
  localCircuitOpen: circuitOpen(),
  localUnavailableUntil: localUnavailableUntil || 0,
  localRoutingEnabled:
    (configuredMode() === SERVER_MODES.OFFLINE_SERVER_ONLY ||
      (configuredMode() === SERVER_MODES.HYBRID && isLocalConfigured())) &&
    !circuitOpen(),
});

const emit = () => {
  if (!hasWindow()) return;
  window.dispatchEvent(new CustomEvent(STATUS_EVENT, { detail: snapshot() }));
};

const cache = () => {
  if (!hasWindow()) return;
  try {
    window.localStorage.setItem(CONFIG_KEY, JSON.stringify({ tenantId: state.tenantId, config: state.config }));
  } catch (_) {}
};

const abortableFetch = async (url, options = {}, timeoutMs = 3000) => {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), Math.max(500, Number(timeoutMs) || 3000)) : null;
  try {
    return await fetch(url, { ...options, ...(controller ? { signal: controller.signal } : {}) });
  } finally {
    if (timer) clearTimeout(timer);
  }
};

const readLocalSession = () => {
  if (!hasWindow()) return null;
  try { return JSON.parse(window.localStorage.getItem(LOCAL_AUTH_SESSION_KEY) || 'null'); } catch (_) { return null; }
};
const readAdminToken = () => {
  if (!hasWindow()) return '';
  try { return String(window.localStorage.getItem(ADMIN_TOKEN_KEY) || ''); } catch (_) { return ''; }
};

const deviceIdentity = () => {
  if (!hasWindow()) return { id: '', name: 'Web Browser' };
  let id = '';
  try {
    id = String(window.localStorage.getItem('coldstorage.deviceId') || '');
    if (!id) {
      id = globalThis.crypto?.randomUUID?.() || `WEB-${Date.now()}-${Math.random().toString(36).slice(2,10)}`;
      window.localStorage.setItem('coldstorage.deviceId', id);
    }
  } catch (_) {}
  let name = '';
  try { name = String(window.localStorage.getItem('coldstorage.deviceName') || ''); } catch (_) {}
  if (!name) name = String(navigator?.platform || navigator?.userAgent || 'Web Browser').slice(0,120);
  return { id, name };
};

const authHeaders = () => {
  const local = state.config.localServer || {};
  const sessionToken = String(readLocalSession()?.sessionToken || '');
  const accessToken = String(local.token || sessionToken || '');
  return {
    'Content-Type': 'application/json',
    'X-Store-Id': String(local.storeId || ''),
    'X-Server-Id': String(local.serverId || ''),
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    ...(state.tenantId ? { 'X-Tenant-Id': state.tenantId } : {}),
    ...(hasWindow() ? (() => { const device = deviceIdentity(); return { 'X-Device-Id': device.id, 'X-Device-Name': device.name }; })() : {}),
  };
};

const localRequest = async (path, options = {}) => {
  const local = state.config.localServer || {};
  const base = normalizeBaseUrl(local.url);
  const { forceLocalProbe = false, ...fetchOptions } = options || {};
  if (!local.enabled || !base) throw new Error('Local store server is not configured.');

  if (
    configuredMode() === SERVER_MODES.HYBRID &&
    !forceLocalProbe &&
    circuitOpen()
  ) {
    const error = new Error('Local store server temporarily unavailable; using cloud fallback.');
    error.code = 'local-circuit-open';
    throw error;
  }

  const started = Date.now();
  try {
    const response = await abortableFetch(`${base}${path}`, {
      ...fetchOptions,
      headers: { ...authHeaders(), ...(fetchOptions.headers || {}) },
    }, local.timeoutMs);
    const text = await response.text();
    let body = null;
    try { body = text ? JSON.parse(text) : null; } catch (_) { body = { message: text }; }
    if (!response.ok) {
      const error = new Error(body?.message || body?.error || `Local server returned HTTP ${response.status}`);
      error.code = String(body?.error || `local-http-${response.status}`);
      error.status = response.status;
      error.details = body;
      throw error;
    }

    consecutiveLocalFailures = 0;
    localUnavailableUntil = 0;
    state.local = { online: true, checkedAt: nowIso(), latencyMs: Date.now() - started, error: '' };
    state.lastError = '';
    emit();
    return body;
  } catch (error) {
    consecutiveLocalFailures += 1;
    const cooldown = Math.min(
      LOCAL_FAILURE_MAX_COOLDOWN_MS,
      LOCAL_FAILURE_BASE_COOLDOWN_MS * Math.max(1, consecutiveLocalFailures)
    );
    if (configuredMode() === SERVER_MODES.HYBRID) {
      localUnavailableUntil = Date.now() + cooldown;
    }
    state.local = { online: false, checkedAt: nowIso(), latencyMs: null, error: String(error?.message || error) };
    state.lastError = state.local.error;
    emit();
    throw error;
  }
};


const adminRequest = async (path, options = {}) => {
  const adminToken = readAdminToken();
  if (!adminToken) throw new Error('This browser has not been paired as a Local Server administrator. Pair the server from Settings first.');
  return localRequest(path, {
    ...options,
    forceLocalProbe: true,
    headers: { ...(options.headers || {}), Authorization: `Bearer ${adminToken}` },
  });
};

const mode = () => configuredMode();
const localFirst = () =>
  mode() === SERVER_MODES.OFFLINE_SERVER_ONLY ||
  (mode() === SERVER_MODES.HYBRID && isLocalConfigured());
// Hybrid reads always have a safe cloud fallback when cloud access is enabled.
// This keeps branding/settings/labels/navigation readable even when a local
// gateway is down. Write fallback remains separately controllable to avoid
// unintended split-brain operational writes.
const canCloudReadFallback = () =>
  mode() === SERVER_MODES.HYBRID &&
  state.config.cloud?.enabled !== false;
const canCloudFallback = () =>
  canCloudReadFallback() &&
  state.config.cloud?.allowFallback !== false;
const shouldSkipLocalReadForCloud = () =>
  canCloudReadFallback() &&
  (!isLocalConfigured() || circuitOpen());
const shouldSkipLocalWriteForCloud = () =>
  canCloudFallback() &&
  (!isLocalConfigured() || circuitOpen());

const service = {
  EVENT: STATUS_EVENT,
  SERVER_MODES,
  DEFAULT_SERVER_CONFIG,

  getSnapshot: snapshot,
  isLocalConfigured,
  canCloudFallback,
  canCloudReadFallback,
  shouldUseLocal() {
    return localFirst() && !circuitOpen();
  },

  subscribe(listener) {
    if (!hasWindow()) return () => {};
    const handler = (event) => listener(event?.detail || snapshot());
    const online = () => {
      state.cloud.online = true;
      emit();
      if (localFirst()) this.testLocal({ silent: true });
    };
    const offline = () => { state.cloud.online = false; emit(); };
    window.addEventListener(STATUS_EVENT, handler);
    window.addEventListener('online', online);
    window.addEventListener('offline', offline);
    listener(snapshot());
    return () => {
      window.removeEventListener(STATUS_EVENT, handler);
      window.removeEventListener('online', online);
      window.removeEventListener('offline', offline);
    };
  },

  setTenantContext(tenantId, offlineConfig = {}) {
    state.tenantId = String(tenantId || state.tenantId || '');
    state.config = mergeConfig(offlineConfig);
    state.cloud.online = typeof navigator === 'undefined' ? true : navigator.onLine !== false;
    cache();
    emit();
    return snapshot();
  },

  setConfig(config = {}) {
    state.config = mergeConfig(config);
    cache();
    emit();
    return snapshot();
  },

  async testLocal({ silent = false, force = false } = {}) {
    if (mode() === SERVER_MODES.ONLINE_ONLY || (mode() === SERVER_MODES.HYBRID && !isLocalConfigured())) {
      state.activeSource = 'cloud';
      state.local = {
        ...state.local,
        online: false,
        checkedAt: state.local.checkedAt || '',
        error: mode() === SERVER_MODES.HYBRID ? 'Local store server is not configured.' : '',
      };
      emit();
      return snapshot();
    }
    if (!force && lastLocalTestAt && Date.now() - lastLocalTestAt < 15000) {
      return snapshot();
    }
    if (!force && localTestInFlight) return localTestInFlight;

    const run = (async () => {
      try {
        const health = await localRequest('/health', { method: 'GET', forceLocalProbe: Boolean(force) });
        state.activeSource = 'local';
        state.local = { ...state.local, online: true, details: health || null };
      } catch (error) {
        state.activeSource = canCloudReadFallback() ? 'cloud' : 'offline';
        if (!silent) throw error;
      } finally {
        lastLocalTestAt = Date.now();
        emit();
      }
      return snapshot();
    })();

    localTestInFlight = run;
    try {
      return await run;
    } finally {
      localTestInFlight = null;
    }
  },

  async getLocalStatus() {
    try {
      const body = await localRequest('/v1/status', { method: 'GET' });
      return body || {};
    } catch (error) {
      if (mode() === SERVER_MODES.OFFLINE_SERVER_ONLY) throw error;
      return null;
    }
  },

  async syncNow() {
    return localRequest('/v1/sync/run', { method: 'POST', body: '{}' });
  },

  async retryFailed() {
    return localRequest('/v1/sync/retry', { method: 'POST', body: '{}' });
  },

  async claimLocalServer({ url, setupCode, tenantId, storeId, serverName } = {}) {
    const base = normalizeBaseUrl(url);
    if (!base) throw new Error('Local server URL is required.');
    const device = deviceIdentity();
    const response = await abortableFetch(`${base}/v1/setup/claim`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(device.id ? { 'X-Device-Id': device.id, 'X-Device-Name': device.name } : {}) },
      body: JSON.stringify({ setupCode, tenantId, storeId, serverName }),
    }, 5000);
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body?.message || `Local server claim failed with HTTP ${response.status}`);
    state.tenantId = String(tenantId || body.tenantId || state.tenantId || '');
    if (hasWindow() && body.adminToken) {
      try { window.localStorage.setItem(ADMIN_TOKEN_KEY, String(body.adminToken)); } catch (_) {}
    }
    state.config = mergeConfig({
      ...state.config,
      localServer: {
        ...state.config.localServer,
        enabled: true,
        url: base,
        token: String(body.token || ''),
        serverId: String(body.serverId || state.config.localServer?.serverId || ''),
        storeId: String(body.storeId || storeId || state.config.localServer?.storeId || ''),
        serverName: String(body.serverName || serverName || state.config.localServer?.serverName || ''),
      },
    });
    cache();
    emit();
    return { ...body, config: state.config };
  },

  async bootstrapSameOrigin({ silent = true } = {}) {
    if (!hasWindow()) return snapshot();
    const origin = normalizeBaseUrl(window.location.origin || '');
    if (!origin) return snapshot();
    try {
      const response = await abortableFetch(`${origin}/health`, { method: 'GET' }, 1800);
      const body = await response.json().catch(() => ({}));
      if (!response.ok || body?.service !== 'frostflow-offline-server' || body?.claimed !== true) return snapshot();
      state.tenantId = String(body.tenantId || state.tenantId || '');
      // Discovering the same-origin LAN server must not silently change a fresh
      // browser from Online Only to Hybrid. Firebase remains the default until
      // the tenant's saved mode is loaded or a local offline session is created.
      const localSession = readLocalSession();
      const discoveredMode = state.config.serverMode === SERVER_MODES.OFFLINE_SERVER_ONLY
        ? SERVER_MODES.OFFLINE_SERVER_ONLY
        : (state.config.serverMode === SERVER_MODES.HYBRID || localSession?.sessionToken
          ? SERVER_MODES.HYBRID
          : SERVER_MODES.ONLINE_ONLY);
      state.config = mergeConfig({
        ...state.config,
        serverMode: discoveredMode,
        localServer: {
          ...state.config.localServer, enabled: true, url: origin, storeId: String(body.storeId || ''), serverId: String(body.serverId || ''), serverName: String(body.serverName || ''),
        },
      });
      cache(); emit();
      return snapshot();
    } catch (error) {
      if (!silent) throw error;
      return snapshot();
    }
  },

  async localAuthLogin(email, password, { sessionHours = 24 } = {}) {
    if (!isLocalConfigured()) await this.bootstrapSameOrigin({ silent: true });
    const local = state.config.localServer || {};
    const base = normalizeBaseUrl(local.url || (hasWindow() ? window.location.origin : ''));
    if (!base || !state.tenantId) throw new Error('Local offline server is not configured for this browser.');
    const device = deviceIdentity();
    const response = await abortableFetch(`${base}/v1/auth/login`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', ...(device.id ? { 'X-Device-Id': device.id, 'X-Device-Name': device.name } : {}) },
      body: JSON.stringify({ tenantId: state.tenantId, email: String(email || '').trim().toLowerCase(), password, sessionHours }),
    }, Math.max(3000, Number(local.timeoutMs || 3000)));
    const body = await response.json().catch(() => ({}));
    if (!response.ok) { const error = new Error(body?.message || 'Offline login failed.'); error.code = `local-auth/${body?.error || response.status}`; throw error; }
    const session = { sessionToken: body.sessionToken, expiresAt: body.expiresAt, user: body.user, tenantId: state.tenantId, serverUrl: base };
    // Successful LAN authentication is an explicit signal that this browser
    // should route company data through the local server. Preserve an explicit
    // Offline Server Only choice; otherwise enter Hybrid for safe cloud resync.
    if (state.config.serverMode !== SERVER_MODES.OFFLINE_SERVER_ONLY) {
      state.config = mergeConfig({ ...state.config, serverMode: SERVER_MODES.HYBRID });
      cache();
      emit();
    }
    if (hasWindow()) {
      try { window.localStorage.setItem(LOCAL_AUTH_SESSION_KEY, JSON.stringify(session)); } catch (_) {}
      window.dispatchEvent(new CustomEvent('coldstorage:local-auth-changed', { detail: session }));
    }
    return session;
  },

  getLocalAuthSession() {
    const session = readLocalSession();
    if (!session) return null;
    if (session.expiresAt && Date.parse(session.expiresAt) <= Date.now()) {
      if (hasWindow()) try { window.localStorage.removeItem(LOCAL_AUTH_SESSION_KEY); } catch (_) {}
      return null;
    }
    return session;
  },

  async localAuthLogout() {
    const session = readLocalSession();
    if (session?.sessionToken && isLocalConfigured()) {
      await localRequest('/v1/auth/logout', { method: 'POST', body: '{}', headers: { Authorization: `Bearer ${session.sessionToken}` } }).catch(() => {});
    }
    if (hasWindow()) {
      try { window.localStorage.removeItem(LOCAL_AUTH_SESSION_KEY); } catch (_) {}
      window.dispatchEvent(new CustomEvent('coldstorage:local-auth-changed', { detail: null }));
    }
  },

  async configureSyncCollections(collectionNames = []) {
    if (!isLocalConfigured()) return { skipped: true, collectionNames: [] };
    return localRequest('/v1/sync/collections', {
      method: 'POST',
      body: JSON.stringify({ collectionNames: Array.from(new Set((collectionNames || []).filter(Boolean))) }),
    });
  },

  async importDocuments(documents = [], { tenantId = state.tenantId } = {}) {
    if (!Array.isArray(documents) || !documents.length || !localFirst() || !isLocalConfigured()) return { skipped: true, imported: 0 };
    let imported = 0;
    let skipped = 0;
    const chunkSize = 200;
    for (let index = 0; index < documents.length; index += chunkSize) {
      const chunk = documents.slice(index, index + chunkSize).map((row) => ({ path: row.path, data: safeJson(row.data), cloudUpdateTime: row.cloudUpdateTime || '' }));
      const result = await localRequest('/v1/firestore/import', { method: 'POST', body: JSON.stringify({ tenantId, documents: chunk }) });
      imported += Number(result?.imported || 0);
      skipped += Number(result?.skipped || 0);
    }
    return { imported, skipped };
  },

  async getAdminConfig() { return adminRequest('/v1/admin/config', { method: 'GET' }); },
  async updateAdminConfig(patch = {}) { return adminRequest('/v1/admin/config', { method: 'POST', body: JSON.stringify(safeJson(patch)) }); },
  async configureCloud({ projectId, storageBucket, serviceAccount } = {}) { return adminRequest('/v1/admin/cloud/config', { method: 'POST', body: JSON.stringify({ projectId, storageBucket, serviceAccount }) }); },
  async testCloud() { return adminRequest('/v1/admin/cloud/test', { method: 'POST', body: '{}' }); },
  async checkDatabase() { return adminRequest('/v1/admin/database/integrity', { method: 'POST', body: '{}' }); },
  async optimizeDatabase() { return adminRequest('/v1/admin/database/optimize', { method: 'POST', body: '{}' }); },
  async listBackups() { return adminRequest('/v1/admin/backups', { method: 'GET' }); },
  async createBackup({ includeFiles = false } = {}) { return adminRequest('/v1/admin/backups/create', { method: 'POST', body: JSON.stringify({ includeFiles: Boolean(includeFiles) }) }); },
  async restoreBackup(id, { restoreFiles = true } = {}) { return adminRequest('/v1/admin/backups/restore', { method: 'POST', body: JSON.stringify({ id, restoreFiles: Boolean(restoreFiles) }) }); },
  async listConflicts(status = 'open') { return adminRequest(`/v1/admin/conflicts?status=${encodeURIComponent(status)}`, { method: 'GET' }); },
  async resolveConflict(id, resolution, mergedData = null) { return adminRequest(`/v1/admin/conflicts/${encodeURIComponent(id)}/resolve`, { method: 'POST', body: JSON.stringify({ resolution, mergedData: safeJson(mergedData) }) }); },
  async listDevices() { return adminRequest('/v1/admin/devices', { method: 'GET' }); },
  async setDeviceStatus(id, status) { return adminRequest(`/v1/admin/devices/${encodeURIComponent(id)}`, { method: 'POST', body: JSON.stringify({ status }) }); },
  async getLogs(limit = 200) { return adminRequest(`/v1/admin/logs?limit=${Math.max(1, Number(limit) || 200)}`, { method: 'GET' }); },
  async restartLocalServer() { return adminRequest('/v1/admin/service/restart', { method: 'POST', body: '{}' }); },
  async listOfflineUsers() { return adminRequest('/v1/admin/offline-users', { method: 'GET' }); },
  async provisionOfflineUser(payload = {}) { return adminRequest('/v1/admin/offline-users/provision', { method: 'POST', body: JSON.stringify(safeJson(payload)) }); },
  async setOfflineUserEnabled(id, enabled) { return adminRequest(`/v1/admin/offline-users/${encodeURIComponent(id)}`, { method: 'POST', body: JSON.stringify({ enabled: Boolean(enabled) }) }); },
  async getWebBuildStatus() { return adminRequest('/v1/admin/web/status', { method: 'GET' }); },
  async deployWebBuild(file) {
    if (!file) throw new Error('React production build ZIP is required.');
    return adminRequest('/v1/admin/web/deploy', { method: 'PUT', body: file, headers: { 'Content-Type': 'application/zip' } });
  },
  async uploadFile({ storagePath, file, metadata = {} } = {}) {
    if (!file || !storagePath) throw new Error('File and storage path are required.');
    const meta = typeof btoa === 'function'
      ? btoa(unescape(encodeURIComponent(JSON.stringify(safeJson(metadata || {})))))
      : '';
    return localRequest(`/v1/files/upload?path=${encodeURIComponent(storagePath)}`, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type || 'application/octet-stream', ...(meta ? { 'X-File-Meta': meta } : {}) },
    });
  },
  async deleteFile(storagePath) {
    if (!storagePath) throw new Error('Storage path is required.');
    return localRequest(`/v1/files/delete?path=${encodeURIComponent(storagePath)}`, { method: 'DELETE' });
  },

  async writeDocument({ path, action = 'set', data = null, merge = false, operationId = '' }) {
    if (!localFirst() || shouldSkipLocalWriteForCloud()) return { handled: false, source: 'cloud' };
    try {
      const body = await localRequest('/v1/firestore/write', {
        method: 'POST',
        body: JSON.stringify({
          operationId: operationId || undefined,
          tenantId: state.tenantId,
          storeId: state.config.localServer?.storeId || '',
          path,
          action,
          merge: Boolean(merge),
          data: safeJson(data),
          client: 'react',
          createdAt: nowIso(),
        }),
      });
      state.activeSource = 'local';
      emit();
      return { handled: true, source: 'local', body };
    } catch (error) {
      if (canCloudFallback()) {
        state.activeSource = 'cloud';
        emit();
        return { handled: false, source: 'cloud', fallbackReason: String(error?.message || error) };
      }
      state.activeSource = 'offline';
      emit();
      throw error;
    }
  },

  async batchWrite(operations = [], { preconditions = [] } = {}) {
    if (!localFirst() || shouldSkipLocalWriteForCloud()) return { handled: false, source: 'cloud' };
    try {
      const body = await localRequest('/v1/firestore/batch', {
        method: 'POST',
        body: JSON.stringify({
          tenantId: state.tenantId,
          storeId: state.config.localServer?.storeId || '',
          operations: operations.map((op) => ({ ...op, data: safeJson(op.data) })),
          preconditions: preconditions.map((row) => ({ path: row.path, version: Number(row.version || 0) })),
          client: 'react',
        }),
      });
      state.activeSource = 'local';
      emit();
      return { handled: true, source: 'local', body };
    } catch (error) {
      if (error?.code === 'transaction-conflict') throw error;
      if (canCloudFallback()) return { handled: false, source: 'cloud', fallbackReason: String(error?.message || error) };
      throw error;
    }
  },

  async readDocument(path) {
    if (!localFirst() || shouldSkipLocalReadForCloud()) return { handled: false, source: 'cloud' };
    try {
      const body = await localRequest(`/v1/firestore/document?path=${encodeURIComponent(path)}`, { method: 'GET' });
      state.activeSource = 'local';
      return { handled: true, found: body?.found !== false, data: reviveJson(body?.data || null), version: Number(body?.version || 0), source: 'local' };
    } catch (error) {
      if (canCloudReadFallback()) return { handled: false, source: 'cloud', fallbackReason: String(error?.message || error) };
      throw error;
    }
  },

  async queryDocuments(spec = {}) {
    if (!localFirst() || shouldSkipLocalReadForCloud()) return { handled: false, source: 'cloud' };
    try {
      const body = await localRequest('/v1/firestore/query', {
        method: 'POST',
        body: JSON.stringify({ ...safeJson(spec), tenantId: state.tenantId }),
      });
      state.activeSource = 'local';
      return { handled: true, rows: Array.isArray(body?.rows) ? body.rows.map((row) => ({ ...row, data: reviveJson(row?.data || row?.payload || {}) })) : [], source: 'local' };
    } catch (error) {
      if (canCloudReadFallback()) return { handled: false, source: 'cloud', fallbackReason: String(error?.message || error) };
      throw error;
    }
  },
};

export { safeJson, reviveJson, mergeConfig };
export default service;
